
import React, { Suspense, useState } from "react"
import { useTranslation } from "react-i18next";
import flag from '../assets/red-flag.svg'
import flagMob from '../assets/flag-mob.svg'
import { useDispatch } from "react-redux";
import { useLoginMutation } from "../store/Apis/UserApi";
import { setAuthenticated } from "../store/slices/UserSlice";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { isValidEmail } from "../utils/formValidation";
import TagManager from "react-gtm-module";

export function Login() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [errors, setErrors] = useState({});
    const [login] = useLoginMutation();
    const navigate = useNavigate();

    const handleSubmit = (event: any) => {
        event.preventDefault();
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length === 0) {
            // Form can be submitted
            login(formData).unwrap().then(data => {
                TagManager.dataLayer({
                    dataLayer: {
                        event: 'login',
                        email: formData.email,
                        user_id: data?.user?.cuid
                    }
                }
                )
                dispatch(setAuthenticated(data))
                navigate('/', { replace: true })
            })
                .catch(err => {
                    console.log(err)
                    if (err.data.error_code)
                        setErrors({ error: t("errors." + err.data.error_code) || err.data.error })
                    else
                        setErrors({ error: err.data.error })
                })
        } else {
            setErrors(validationErrors);
        }
    }
    const validateInput = (name: string, value: string) => {
        switch (name) {
            case "email":
                return isValidEmail(value)
                    ? null
                    : t("errors.EMAIL_INVALID") || "Please enter a valid email address";
            case "password":
                return value && value.length > 0
                    ? null
                    : t("errors.PASSWORD_REQUIRED") || "Please enter your password";
            default:
                return null;
        }
    };

    const validateForm = () => {
        const validationErrors: any = {};
        for (const [name, value] of Object.entries(formData)) {
            const err = validateInput(name, value)
            if (err)
                validationErrors[name] = err;
        }
        return validationErrors;
    };

    const handleChange = (event: any) => {
        setFormData({ ...formData, [event.target.name]: event.target.value });
        setErrors({ ...errors, [event.target.name]: validateInput(event.target.name, event.target.value) });
    };




    return (
        <Suspense>
            <Helmet>
                <link rel="canonical" href={`https://${process.env.REACT_APP_DOMAIN_NAME}/login`} />
                <title>Login</title>
                <meta name="description" content="Login to your account."></meta>
            </Helmet>
            <div className="w-screen h-screen  web:overflow-hidden">
                <div className="flex flex-col w-full h-full bg-orange web:visible phone:hidden">
                    <h1 className="w-[100%] text-center pt-20 pb-8 text-7xl leading-snug text-red font-bold font-Inter">{t('login.title')}</h1>
                    <div className="w-[90%] flex flex-row mt-32">
                        <form onSubmit={handleSubmit} className="w-[50%] ms-10  flex flex-col items-end">
                            <input type="email" className="w-full h-16 px-6 border-2 shadow-lg bg-landing-yellow border-red font-Inter placeholder:text-rose-500" name="email" value={formData.email} onChange={handleChange} placeholder={t('login.email') || "email"} required></input>
                            <input type="password" className="w-full h-16 px-6 mt-4 border-2 shadow-lg bg-landing-yellow border-red font-Inter placeholder:text-rose-500" placeholder={t('login.pwd') || "password"} name="password" value={formData.password} onChange={handleChange} required></input>
                            <a href="/reset" className="pt-2 text-lg text-red font-Inter hover:underline">{t('login.forgot')}</a>
                            <p className="w-full pt-3 text-lg text-red font-Inter text-end">
                                {t('login.no_account')}{' '}
                                <Link to="/register" className="font-bold underline decoration-2 underline-offset-2 hover:opacity-90 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red focus-visible:ring-offset-2 focus-visible:ring-offset-orange">
                                    {t('login.sign_up')}
                                </Link>
                            </p>
                            {(Object.entries(errors).length > 0) && <ul className="flex flex-col items-center justify-center w-full">
                                {Object.entries(errors).map(([key, value], index) => {
                                    if (value)
                                        return (<li key={index} className="text-red text-sm font-bold w-[98%] " >{value.toString()}</li>)
                                    else
                                        return (null);
                                })}
                            </ul>}
                            <input className="w-[25%] h-14 bg-light-blue p-2 mt-8 border-2 font-Inter font-bold border-black shadow-lg" type="submit" value={t('login.enter') || "login"} />
                        </form>
                        <div className="flex flex-col items-end justify-end h-full grow">
                            <img alt="flag" className="grow w-[90%]" src={flag}></img>
                        </div>
                    </div>
                </div>
                <form onSubmit={handleSubmit} className="relative flex flex-col items-center justify-start w-screen h-screen bg-red web:hidden phone:visible ">
                    <h1 className="z-30 w-[74%] mt-16 mb-8 text-5xl leading-snug text-landing-yellow font-bold font-Inter">{t('login.title')}</h1>
                    <div className="z-30 mt-2 flex w-full flex-col items-center">
                        <input type="email" className="w-[90%] h-16 bg-landing-yellow border-2 border-red font-Inter shadow-lg shadow-black placeholder:text-rose-500 px-6" placeholder={t('login.email') || "email"} name="email" value={formData.email} onChange={handleChange} ></input>
                        <input type="password" className="w-[90%] h-16 bg-landing-yellow border-2 border-red font-Inter shadow-lg shadow-black placeholder:text-rose-500 px-6 mt-4" placeholder={t('login.pwd') || "password"} name="password" value={formData.password} onChange={handleChange} ></input>
                        <a href="/reset" className="p-4 text-sm text-center text-landing-yellow font-Inter">{t('login.forgot')}</a>
                        <p className="z-30 px-4 pt-1 text-center text-sm text-landing-yellow font-Inter">
                            {t('login.no_account')}{' '}
                            <Link to="/register" className="font-bold underline decoration-2 underline-offset-2 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-landing-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-red">
                                {t('login.sign_up')}
                            </Link>
                        </p>
                    </div>
                    <input className="w-[70%] h-14 bg-light-blue p-2 mt-8 border-2 font-Inter font-bold border-black shadow-lg mb-4 z-30" type="submit" value={t('login.enter') || "login"} />
                    <img alt="flag orange" className="absolute top-[25%] z-10" src={flagMob}></img>
                </form>
            </div >
        </Suspense >
    )
}